import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Typography, Card, CardActionArea, CardContent, Box } from '@mui/material';
import { DynamicIcon } from '../../components/icons';
import { PageHeader } from '../../components/Common';

const MyAppsPage = ({ appBundles }) => {
    const { bundleId } = useParams();

    const activeBundle = bundleId
        ? appBundles.find(b => b.id === bundleId)
        : null;

    const itemsToDisplay = activeBundle ? activeBundle.contents : appBundles;
    const pageTitle = activeBundle ? activeBundle.name : "My Apps";

    return (
        <div>
            <PageHeader title={pageTitle}>
                {activeBundle && (
                    <Link to="/my-apps" className="flex items-center text-primary hover:underline">
                        <DynamicIcon name="ArrowLeft" className="mr-2 h-4 w-4" />
                        Back to All Apps
                    </Link>
                )}
            </PageHeader>

            <Typography variant="body1" className="mb-6 text-gray-600 dark:text-gray-400">
                {activeBundle
                    ? `Showing all content within the "${activeBundle.name}" bundle.`
                    : "Access your personalized suite of analytics and business tools designed to drive insights and efficiency."
                }
            </Typography>

            {itemsToDisplay && itemsToDisplay.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {itemsToDisplay.map(item => {
                        let linkPath;
                        if (item.isBundle) {
                            // User Request Mod: Direct navigation to App Runner (About Page) for bundles too
                            linkPath = `/tenant/app/${item.id}`;
                        } else if (bundleId === 'individual-apps') {
                            // Fix: Items in 'individual-apps' are instances, so link to them directly
                            linkPath = `/tenant/app/${item.id}`;
                        } else {
                            // Standard bundle content
                            linkPath = `/tenant/app/${bundleId || item.id}/${item.id}`;
                        }

                        return (
                            <Card
                                key={item.id}
                                className="shadow-lg transition-transform transform hover:-translate-y-1 overflow-hidden h-full dark:bg-gray-800 dark:border-gray-700"
                                sx={{ height: '290px', display: 'flex', flexDirection: 'column' }}
                            >
                                <CardActionArea component={Link} to={linkPath} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                                    <div className="flex flex-col items-center text-center p-4 h-full overflow-hidden relative w-full">
                                        <DynamicIcon
                                            name={item.icon || 'Package'}
                                            size={40}
                                            className="mb-3 text-primary"
                                        />
                                        <Typography variant="h6" className="mb-2 w-full break-words text-center dark:text-white" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {item.name}
                                        </Typography>

                                        {/* ✅ reverted clean truncation — now limited to 6 lines */}
                                        <Typography
                                            variant="body2"
                                            className="text-sm text-gray-500 dark:text-gray-300 overflow-hidden mb-2"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 6, // ✅ reduced from 7 to 6
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: '1.4em',
                                                maxHeight: '8.4em', // 6 × 1.4em
                                                textOverflow: 'ellipsis',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {item.description ||
                                                (item.isBundle
                                                    ? `${item.contents?.length || 0} items`
                                                    : 'Application Content')}
                                        </Typography>
                                    </div>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col justify-center items-center h-64 text-gray-500">
                    <DynamicIcon name="PackageOpen" className="w-12 h-12 mb-4 opacity-50" />
                    <Typography>No applications have been assigned to your tenant yet.</Typography>
                </div>
            )}
        </div>
    );
};

export default MyAppsPage;
